const SECOND_IN_MS = 1e3;
const MINUTE_IN_MS = 60 * SECOND_IN_MS;
const HOUR_IN_MS = 60 * MINUTE_IN_MS;
const DAY_IN_MS = 24 * HOUR_IN_MS;

export type { FormatterOptions, RelativeTimeComputation } from '@sweetistics/contracts/utils/time';
export {
  computeRelativeTime,
  formatCompactRelativeTime,
  formatRelativeTime,
  formatRelativeTimeAgo,
  getRelativeTimeResult,
} from '@sweetistics/contracts/utils/time';

import type { FormatterOptions } from '@sweetistics/contracts/utils/time';
import {
  formatCompactRelativeTime as sharedFormatCompactRelativeTime,
  getRelativeTimeResult as sharedGetRelativeTimeResult,
} from '@sweetistics/contracts/utils/time';

function resolveTimestamp(input: Date | string | number | undefined | null): number | null {
  if (input === undefined) {
    return null;
  }
  if (typeof input === 'number') {
    if (!Number.isFinite(input)) {
      return null;
    }
    return input;
  }
  if (typeof input === 'string') {
    const value = Date.parse(input);
    return Number.isNaN(value) ? null : value;
  }
  if (typeof input === 'object' && input !== null) {
    const candidate = (input as { valueOf(): unknown }).valueOf();
    if (typeof candidate === 'number') {
      return Number.isNaN(candidate) ? null : candidate;
    }
  }
  return null;
}

export interface FormatRelativeTimeShortOptions extends FormatterOptions {
  fallback?: string;
}

/**
 * Produce compact relative phrases like "5m ago" or "in 2h".
 */
export function formatRelativeTimeShort(
  date: Date | string | number | undefined | null,
  { fallback = 'unknown time', ...options }: FormatRelativeTimeShortOptions = {}
): string {
  const hasIntlRelativeTime = typeof Intl !== 'undefined' && typeof Intl.RelativeTimeFormat === 'function';
  const timestamp = resolveTimestamp(date);
  if (hasIntlRelativeTime) {
    const result = sharedGetRelativeTimeResult(date, options);
    if (!result) {
      return fallback;
    }
    if (result.value === 0 && result.unit === 'second') {
      return 'just now';
    }

    const compact = sharedFormatCompactRelativeTime(result);
    if (result.value > 0) {
      return compact;
    }
    if (compact === 'now') {
      return 'just now';
    }
    return `${compact} ago`;
  }

  if (timestamp === null) {
    return fallback;
  }

  const nowMs = options.nowMs ?? Date.now();
  const diffMs = timestamp - nowMs;
  const absMs = Math.abs(diffMs);

  if (absMs < MINUTE_IN_MS) {
    return 'just now';
  }
  if (absMs < HOUR_IN_MS) {
    const minutes = Math.max(1, Math.round(absMs / MINUTE_IN_MS));
    return diffMs > 0 ? `in ${minutes}m` : `${minutes}m ago`;
  }
  if (absMs < DAY_IN_MS) {
    const hours = Math.max(1, Math.round(absMs / HOUR_IN_MS));
    return diffMs > 0 ? `in ${hours}h` : `${hours}h ago`;
  }
  const days = Math.max(1, Math.round(absMs / DAY_IN_MS));
  return diffMs > 0 ? `in ${days}d` : `${days}d ago`;
}

export interface AbsoluteRelativeFormatOptions extends FormatterOptions {
  /** Options passed to Intl.DateTimeFormat for the absolute label */
  absoluteFormat?: Intl.DateTimeFormatOptions;
}

export interface AbsoluteRelativeFormatResult {
  absolute: string;
  relative: string | null;
  timestamp: number;
}

export function getAbsoluteRelativeFormat(
  date: Date | string | number | undefined | null,
  { locale, style, numeric, nowMs, absoluteFormat }: AbsoluteRelativeFormatOptions = {}
): AbsoluteRelativeFormatResult | null {
  const timestamp = resolveTimestamp(date);
  if (timestamp === null) {
    return null;
  }

  const absoluteOptions = absoluteFormat ?? { dateStyle: 'medium', timeStyle: 'short' };
  let absolute: string;
  try {
    // timestamp is already a number (milliseconds), pass it directly to format
    absolute = new Intl.DateTimeFormat(locale, absoluteOptions).format(timestamp);
  } catch {
    // Fallback: use DateTimeFormat's format method with the timestamp number
    try {
      absolute = new Intl.DateTimeFormat(locale).format(timestamp);
    } catch {
      // Last resort: stringify the millisecond timestamp
      absolute = `${timestamp}`;
    }
  }

  const referenceNow = nowMs ?? Date.now();

  const relativeOptions: FormatterOptions = {
    nowMs: referenceNow,
  };
  if (locale) {
    relativeOptions.locale = locale;
  }
  if (style) {
    relativeOptions.style = style;
  }
  if (numeric) {
    relativeOptions.numeric = numeric;
  }

  const relativeResult = sharedGetRelativeTimeResult(timestamp, relativeOptions);
  let relative: string | null = null;
  if (relativeResult) {
    relative = relativeResult.value === 0 && relativeResult.unit === 'second' ? 'just now' : relativeResult.label;
  }

  return { timestamp, absolute, relative };
}

export interface FormatAbsoluteRelativeOptions extends AbsoluteRelativeFormatOptions {
  combineRelative?: boolean;
  fallback?: string;
}

export function formatAbsoluteRelative(
  date: Date | string | number | undefined | null,
  { fallback = 'Never', combineRelative = true, ...options }: FormatAbsoluteRelativeOptions = {}
): string {
  const result = getAbsoluteRelativeFormat(date, options);
  if (!result) {
    return fallback;
  }

  if (!(combineRelative && result.relative)) {
    return result.absolute;
  }

  return `${result.absolute} (${result.relative})`;
}

export interface FormatDurationMsOptions {
  /** String to use when the input is null, undefined, NaN, or negative. Defaults to '-' */
  fallback?: string;
  /** Omit seconds from the formatted string when true. Defaults to false. */
  showSeconds?: boolean;
}

export interface FormatLatencyMsOptions {
  fallback?: string;
  minutePrecision?: number;
  secondPrecision?: number;
}

export function formatLatencyMs(
  durationMs: number | null | undefined,
  { fallback = 'N/A', secondPrecision = 1, minutePrecision = 1 }: FormatLatencyMsOptions = {}
): string {
  if (durationMs === null || durationMs === undefined || Number.isNaN(durationMs) || durationMs < 0) {
    return fallback;
  }

  if (durationMs < SECOND_IN_MS) {
    return `${Math.round(durationMs)}ms`;
  }

  if (durationMs < MINUTE_IN_MS) {
    const seconds = durationMs / SECOND_IN_MS;
    return `${seconds.toFixed(secondPrecision)}s`;
  }

  const minutes = durationMs / MINUTE_IN_MS;
  return `${minutes.toFixed(minutePrecision)}m`;
}

export function formatDurationMs(
  durationMs: number | null | undefined,
  { fallback = '-', showSeconds = true }: FormatDurationMsOptions = {}
): string {
  if (durationMs === null || durationMs === undefined || Number.isNaN(durationMs) || durationMs < 0) {
    return fallback;
  }

  const totalSeconds = Math.floor(durationMs / SECOND_IN_MS);
  const totalMinutes = Math.floor(totalSeconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }

  if (minutes > 0) {
    if (!showSeconds) {
      return `${minutes}m`;
    }
    return `${minutes}m ${seconds}s`;
  }

  if (!showSeconds) {
    return minutes > 0 ? `${minutes}m` : '0m';
  }

  return `${totalSeconds}s`;
}

export function toTimestamp(input: Date | string | number | undefined | null): number | null {
  return resolveTimestamp(input);
}

const PROMPT_UTC_FORMATTER = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
  timeZone: 'UTC',
});

const REQUIRED_PROMPT_PARTS: Intl.DateTimeFormatPartTypes[] = ['weekday', 'month', 'day', 'year', 'hour', 'minute'];

export function formatUtcTimestampForPrompt(date: Date): string {
  const epoch = date.getTime();
  if (Number.isNaN(epoch)) {
    throw new TypeError('formatUtcTimestampForPrompt received an invalid Date');
  }

  const parts = PROMPT_UTC_FORMATTER.formatToParts(date);

  const resolved = REQUIRED_PROMPT_PARTS.map((type) => {
    const match = parts.find((part) => part.type === type);
    if (!match) {
      throw new TypeError(`Missing ${type} part while formatting UTC timestamp`);
    }
    return match.value;
  });

  const [weekday, month, day, year, hour, minute] = resolved;
  return `${weekday} ${month} ${day} ${year} ${hour}:${minute}`;
}

export function formatVideoDuration(durationMs: number | null): string | null {
  if (!(durationMs && Number.isFinite(durationMs))) {
    return null;
  }
  const totalSeconds = Math.max(0, Math.floor(durationMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  }
  return `${minutes}:${seconds.toString().padStart(2, '0')}`;
}
